import { useState, useEffect } from 'react';

interface SquadPlayer {
  id: number;
  name: string;
  position: string;
  nationality: string;
  dateOfBirth?: string;
  shirtNumber?: number;
}

interface RecentMatch {
  date: string;
  opponent: string;
  isHome: boolean;
  result: string;
  score: string;
  competition: string;
}

interface TeamDetail {
  id: number;
  name: string;
  shortName: string;
  crest: string;
  venue?: string;
  founded?: number;
  website?: string;
  coach?: string;
  squad: SquadPlayer[];
}

interface Props {
  teamId: number;
  teamName: string;
  teamCrest: string;
  league: string;
  onClose: () => void;
}

const POSITION_ORDER: Record<string, number> = {
  Portero: 0, Defensa: 1, Centrocampista: 2, Delantero: 3,
};

const POSITION_COLORS: Record<string, string> = {
  Portero: '#ffd700',
  Defensa: '#60a5fa',
  Centrocampista: '#00e87a',
  Delantero: '#ff6b35',
};

function calcAge(dob?: string): string {
  if (!dob) return '—';
  const birth = new Date(dob);
  const age = new Date().getFullYear() - birth.getFullYear();
  return String(age);
}

export default function TeamDetailModal({ teamId, teamName, teamCrest, league, onClose }: Props) {
  const [detail, setDetail] = useState<TeamDetail | null>(null);
  const [matches, setMatches] = useState<RecentMatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<'plantilla' | 'resultados'>('plantilla');
  const [imgError, setImgError] = useState(false);

  const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(
          `${supabaseUrl}/functions/v1/football-stats?type=team-detail&team=${teamId}&league=${league}&season=2024`
        );
        const data = await res.json();
        if (data.status === 'ok' && data.team) {
          setDetail(data.team);
          setMatches(data.recentMatches ?? []);
        } else {
          setError(data.message ?? 'No se pudo cargar la ficha del equipo.');
        }
      } catch {
        setError('Error de conexión. Configura FOOTBALL_DATA_API_KEY para acceder a fichas de equipos.');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [teamId, league, supabaseUrl]);

  // Group squad by position
  const squadByPosition = (detail?.squad ?? []).reduce<Record<string, SquadPlayer[]>>((acc, p) => {
    const pos = p.position || 'Sin posición';
    if (!acc[pos]) acc[pos] = [];
    acc[pos].push(p);
    return acc;
  }, {});

  const positionGroups = Object.entries(squadByPosition).sort(
    ([a], [b]) => (POSITION_ORDER[a] ?? 9) - (POSITION_ORDER[b] ?? 9)
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm"></div>
      <div
        className="relative bg-[#0d0f14] border border-[#2a2d3a] rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-4 px-6 py-5 border-b border-[#1e2028] flex-shrink-0">
          <div className="w-14 h-14 flex items-center justify-center flex-shrink-0">
            {teamCrest && !imgError ? (
              <img src={teamCrest} alt={teamName} className="w-14 h-14 object-contain" onError={() => setImgError(true)} />
            ) : (
              <div className="w-12 h-12 rounded-full bg-[#2a2d3a] flex items-center justify-center">
                <i className="ri-shield-line text-[#6b7280] text-xl"></i>
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h2 translate="no" className="text-white font-black text-xl truncate">{teamName}</h2>
            {detail && (
              <div className="flex items-center gap-3 mt-1 flex-wrap">
                {detail.venue && (
                  <span className="flex items-center gap-1 text-[#6b7280] text-xs">
                    <i className="ri-map-pin-line text-xs"></i>
                    {detail.venue}
                  </span>
                )}
                {detail.founded && (
                  <span className="flex items-center gap-1 text-[#6b7280] text-xs">
                    <i className="ri-calendar-line text-xs"></i>
                    Fund. {detail.founded}
                  </span>
                )}
                {detail.coach && (
                  <span className="flex items-center gap-1 text-[#ffd700] text-xs font-semibold">
                    <i className="ri-user-star-line text-xs"></i>
                    {detail.coach}
                  </span>
                )}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-xl bg-[#1e2028] text-[#6b7280] hover:text-white hover:bg-[#2a2d3a] transition-colors cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Sub-tabs */}
        <div className="flex gap-1 px-5 py-3 border-b border-[#1e2028] flex-shrink-0">
          {([
            { key: 'plantilla', label: 'Plantilla', icon: 'ri-group-line' },
            { key: 'resultados', label: 'Últimos resultados', icon: 'ri-trophy-line' },
          ] as const).map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveSection(t.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                activeSection === t.key
                  ? 'bg-[#ffd700]/15 text-[#ffd700]'
                  : 'text-[#6b7280] hover:text-white'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${t.icon} text-sm`}></i>
              </div>
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="w-8 h-8 border-2 border-[#ffd700]/30 border-t-[#ffd700] rounded-full animate-spin"></div>
              <p className="text-[#6b7280] text-sm">Cargando ficha del equipo...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-16 px-6 gap-4 text-center">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#ef4444]/10">
                <i className="ri-error-warning-line text-[#ef4444] text-2xl"></i>
              </div>
              <p className="text-[#a8b2c1] text-sm max-w-sm leading-relaxed">{error}</p>
              <div className="bg-[#151719] border border-[#2a2d3a] rounded-xl p-4 text-left max-w-sm">
                <p className="text-[#ffd700] text-xs font-bold mb-2">Para activar fichas de equipos:</p>
                <ol className="text-[#6b7280] text-xs space-y-1 list-decimal list-inside">
                  <li>Regístrate en <span className="text-[#00e87a]">football-data.org</span> (gratis)</li>
                  <li>Copia tu API Token del dashboard</li>
                  <li>En Supabase → Edge Functions → Secrets</li>
                  <li>Añade <span className="text-white font-mono">FOOTBALL_DATA_API_KEY</span></li>
                </ol>
              </div>
            </div>
          ) : (
            <>
              {/* ── PLANTILLA ─────────────────────────────────────── */}
              {activeSection === 'plantilla' && (
                <div className="p-5 space-y-5">
                  {positionGroups.length === 0 ? (
                    <div className="text-center py-10">
                      <p className="text-[#6b7280] text-sm">Plantilla no disponible.</p>
                    </div>
                  ) : (
                    positionGroups.map(([pos, posPlayers]) => (
                      <div key={pos}>
                        <div className="flex items-center gap-2 mb-2">
                          <span
                            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{ background: POSITION_COLORS[pos] ?? '#6b7280' }}
                          ></span>
                          <h5
                            className="text-xs font-bold uppercase tracking-widest"
                            style={{ color: POSITION_COLORS[pos] ?? '#6b7280' }}
                          >
                            {pos} ({posPlayers.length})
                          </h5>
                        </div>
                        <div className="bg-[#151719] border border-[#2a2d3a] rounded-xl overflow-hidden">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-[#1e2028]">
                                <th className="w-10 px-3 py-2.5 text-center text-[#3d4149] text-xs">#</th>
                                <th className="px-4 py-2.5 text-left text-[#6b7280] text-xs font-semibold uppercase">Jugador</th>
                                <th className="px-3 py-2.5 text-center text-[#6b7280] text-xs font-semibold uppercase">Nac.</th>
                                <th className="px-3 py-2.5 text-center text-[#6b7280] text-xs font-semibold uppercase">Edad</th>
                              </tr>
                            </thead>
                            <tbody>
                              {posPlayers
                                .sort((a, b) => (a.shirtNumber ?? 99) - (b.shirtNumber ?? 99))
                                .map((p) => (
                                  <tr key={p.id} className="border-b border-[#1e2028]/40 last:border-0 hover:bg-white/[0.02] transition-colors">
                                    <td className="px-3 py-2.5 text-center text-[#3d4149] text-sm font-mono">
                                      {p.shirtNumber ?? '—'}
                                    </td>
                                    <td className="px-4 py-2.5">
                                      <span translate="no" className="text-white text-sm">{p.name}</span>
                                    </td>
                                    <td className="px-3 py-2.5 text-center text-[#a8b2c1] text-xs">{p.nationality ?? '—'}</td>
                                    <td className="px-3 py-2.5 text-center text-[#6b7280] text-sm">{calcAge(p.dateOfBirth)}</td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* ── ÚLTIMOS RESULTADOS ────────────────────────────── */}
              {activeSection === 'resultados' && (
                <div className="p-5">
                  {matches.length === 0 ? (
                    <div className="text-center py-10">
                      <p className="text-[#6b7280] text-sm">Sin partidos recientes disponibles.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {[...matches].reverse().map((m, idx) => {
                        const resultColors: Record<string, string> = { W: '#00e87a', L: '#ef4444', D: '#ffd700', '—': '#6b7280' };
                        const resultLabels: Record<string, string> = { W: 'V', L: 'D', D: 'E', '—': '—' };
                        const rc = resultColors[m.result] ?? '#6b7280';
                        return (
                          <div
                            key={idx}
                            className="flex items-center gap-4 bg-[#151719] border border-[#2a2d3a] rounded-xl px-4 py-3"
                          >
                            {/* Result badge */}
                            <div
                              className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-black flex-shrink-0"
                              style={{ background: `${rc}18`, color: rc }}
                            >
                              {resultLabels[m.result] ?? '—'}
                            </div>

                            {/* Match info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-[#6b7280] text-xs">{m.isHome ? 'vs' : '@'}</span>
                                <span translate="no" className="text-white text-sm font-semibold truncate">{m.opponent}</span>
                                {m.isHome && (
                                  <span className="px-1.5 py-0.5 rounded bg-[#00e87a]/10 text-[#00e87a] text-[10px] font-bold">LOCAL</span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-[#3d4149] text-xs">{m.date}</span>
                                {m.competition && (
                                  <span className="text-[#3d4149] text-xs">· {m.competition}</span>
                                )}
                              </div>
                            </div>

                            {/* Score */}
                            <div className="text-right flex-shrink-0">
                              <span
                                className="text-base font-black tabular-nums"
                                style={{ color: rc }}
                              >
                                {m.score}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-[#1e2028] flex items-center justify-between flex-shrink-0">
          <span className="text-[#3d4149] text-xs">football-data.org · Temporada 2024/25</span>
          {detail?.website && (
            <a
              href={detail.website}
              target="_blank"
              rel="nofollow noreferrer"
              className="text-[#6b7280] hover:text-white text-xs flex items-center gap-1 transition-colors cursor-pointer"
            >
              Web oficial <i className="ri-external-link-line text-xs"></i>
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
